import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Scan, Maximize2, Minimize2 } from "lucide-react";
import { fmtIDR } from "@/lib_app/api";
import { terbilang, num } from "./QuotationDetailModal";

const A4_W = 794;
const A4_H = 1123;
const PAD = 36;
const NAVY = "#3B4A63";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const dMY = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return `${d} ${MONTHS[Number(m) - 1] || ""} ${y}`;
};
const fmtQty = (v) => num(v).toFixed(2).replace(/0+$/, "").replace(/\.$/, "").replace(".", ",");

const sMetaLbl = { fontSize: 9, color: "#8A8F98", padding: "2px 12px 2px 0", width: 92, whiteSpace: "nowrap" };
const sMetaVal = { fontSize: 10, fontWeight: 700, padding: "2px 0" };
const sBox = { background: "#F4F6FA", padding: "12px 14px", verticalAlign: "top", width: "50%", borderRadius: 10 };
const sBoxTtl = { display: "inline-block", background: "#fff", borderRadius: 20, padding: "5px 12px 4px", fontSize: 8, lineHeight: 1, letterSpacing: 1.6, color: NAVY, fontWeight: 700, marginBottom: 8 };
const sKvK = { fontSize: 9.5, color: "#8A8F98", padding: "2px 12px 2px 0", verticalAlign: "top", width: 74, whiteSpace: "nowrap" };
const sKvV = { fontSize: 9.5, padding: "2px 0", verticalAlign: "top" };
const sTh = { background: NAVY, color: "#fff", fontSize: 8.5, letterSpacing: 1.2, padding: 9, textAlign: "left" };
const sTd = { padding: "8px 10px", fontSize: 10, borderBottom: "1px solid #E7EAF0", verticalAlign: "top" };
const sTdR = { ...sTd, textAlign: "right", whiteSpace: "nowrap" };
const INTRO_TAIL = " hereby issues this Purchase Order for the goods and/or services detailed below, subject to the specifications, prices, delivery schedule, and terms stated herein and in its attachments.";
const sSectHead = { fontSize: 8, letterSpacing: 2, color: NAVY, fontWeight: 700, borderBottom: "1px solid #E1E6EF", paddingBottom: 5, marginBottom: 8 };

const KidRows = ({ item }) => {
  const kids = (item.children || []).filter((c) => (c || "").trim());
  if (!kids.length) return null;
  return (
    <div style={{ paddingLeft: 12, marginTop: 2 }}>
      {kids.map((c, i) => (
        <div key={i} style={{ display: "flex", gap: 5 }}>
          <span style={{ color: "#9AA0AC", fontSize: 9, lineHeight: 1.5, flexShrink: 0 }}>&middot;</span>
          <span style={{ color: "#9AA0AC", fontSize: 9, lineHeight: 1.5 }}>{c}</span>
        </div>
      ))}
    </div>
  );
};

const Sheet = ({ children, innerRef }) => (
  <div
    ref={innerRef}
    style={{
      width: A4_W, minHeight: A4_H, padding: PAD, background: "#fff",
      fontFamily: "'DM Sans', Arial, Helvetica, sans-serif", color: "#14161F", fontSize: 10.5, lineHeight: 1.4,
      boxShadow: "0 12px 40px -12px rgba(20,22,31,0.35)", display: "flex", flexDirection: "column",
    }}
  >{children}</div>
);

const Footer = ({ company, page }) => (
  <div style={{ marginTop: "auto", paddingTop: 20 }}>
    <div style={{ borderTop: "1px solid #E7EAF0", paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 9, fontWeight: 700, color: NAVY, letterSpacing: 0.3 }}>Your Creative-Tech Partner</div>
        <div style={{ fontSize: 7, color: "#A0A3A8", marginTop: 2 }}>Generated by IPTEC&reg; Supersales App.</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 8, color: NAVY, fontWeight: 700, letterSpacing: 0.3 }}>{company?.web || "www.iptec.id"}</div>
        <div style={{ fontSize: 7, color: "#A0A3A8", marginTop: 2 }}>Page {page} of 2</div>
      </div>
    </div>
  </div>
);

const Brand = ({ company, size = 270 }) => (company?.logo
  ? <img src={company.logo} alt="logo" style={{ maxHeight: 74, maxWidth: size, borderRadius: 10, display: "inline-block" }} />
  : <div style={{ fontSize: 17, fontWeight: 700 }}>{company?.name || "Purchase Order"}</div>);

export function PoPdfPreview({ form, totals, company, ownerName, ownerJobTitle, party, qrDataUri, full, onToggleFull }) {
  const wrapRef = useRef(null);
  const [zoom, setZoom] = useState(0.62);
  const [scale, setScale] = useState(0.62);
  const [wrapW, setWrapW] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const avail = el.clientWidth - 32;
      setWrapW(el.clientWidth);
      setScale(zoom === "fit" ? Math.max(0.2, Math.min(1, avail / A4_W)) : zoom);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoom, full]);

  const step = (d) => setZoom((z) => {
    const cur = z === "fit" ? scale : z;
    return Math.max(0.3, Math.min(1.4, Math.round((cur + d) * 20) / 20));
  });

  const groups = form.groups || [];
  const vendor = { name: party?.name, address: form.vendor_address ?? party?.address, contact: form.vendor_contact ?? party?.contact };
  const shipTo = (form.ship_to || "").trim() || company?.address || "-";
  const termGroups = (form.terms_groups || []).filter((g) => (g.title || "").trim() || (g.items || []).some((t) => (t || "").trim()));
  const stamp = form.status === "draft" ? "DRAFT" : form.status === "void" ? "VOID" : form.status === "paid" ? "PAID" : "";

  const rows = [];
  let alt = false;
  groups.forEach((g, gi) => {
    const gsub = totals.gsubs?.[gi] || 0;
    const bg = alt ? "#F7F8FB" : "transparent";
    alt = !alt;
    const cell = (extra = {}) => ({ ...sTd, background: bg, ...extra });
    const volCell = (qty, unit) => (
      <td style={cell({ width: 74, textAlign: "center", color: "#5B616E" })}>{fmtQty(qty)} {unit}</td>
    );

    if (g.price_mode !== "itemized") {
      const subs = (g.items || []).filter((it) => (it.description || "").trim() || (it.children || []).some((c) => (c || "").trim()));
      rows.push(
        <tr key={`g${gi}`}>
          <td style={cell({ textAlign: "center", width: 24, color: "#8A8F98" })}>{gi + 1}</td>
          <td style={cell()}>
            <b style={{ fontSize: 10.5 }}>{g.title}</b>
            {subs.map((it, ii) => (
              <div key={ii}>
                {(it.description || "").trim() && (
                  <div style={{ display: "flex", gap: 5, marginTop: 2 }}>
                    <span style={{ color: "#7A8090", fontSize: 9.5, lineHeight: 1.5, flexShrink: 0 }}>&ndash;</span>
                    <span style={{ color: "#7A8090", fontSize: 9.5, lineHeight: 1.5 }}>{it.description}</span>
                  </div>
                )}
                <KidRows item={it} />
              </div>
            ))}
          </td>
          {volCell(g.qty, g.unit)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(g.price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(gsub)}</td>
        </tr>
      );
      return;
    }

    const items = g.items || [];
    const single = items.length === 1 && !(items[0].description || "").trim();
    if (single) {
      rows.push(
        <tr key={`g${gi}`}>
          <td style={cell({ textAlign: "center", width: 24, color: "#8A8F98" })}>{gi + 1}</td>
          <td style={cell()}><b style={{ fontSize: 10.5 }}>{g.title}</b><KidRows item={items[0]} /></td>
          {volCell(items[0].qty, items[0].unit)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(items[0].price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(num(items[0].qty) * num(items[0].price))}</td>
        </tr>
      );
      return;
    }

    rows.push(
      <tr key={`g${gi}`}>
        <td style={cell({ textAlign: "center", width: 24, color: "#8A8F98" })}>{gi + 1}</td>
        <td style={cell()} colSpan={4}><b style={{ fontSize: 10.5 }}>{g.title}</b></td>
      </tr>
    );
    items.forEach((it, ii) => rows.push(
      <tr key={`g${gi}i${ii}`}>
        <td style={cell()} />
        <td style={cell()}>{it.description}<KidRows item={it} /></td>
        {volCell(it.qty, it.unit)}
        <td style={{ ...sTdR, background: bg }}>{fmtIDR(it.price)}</td>
        <td style={{ ...sTdR, background: bg }}>{fmtIDR(num(it.qty) * num(it.price))}</td>
      </tr>
    ));
    rows.push(
      <tr key={`g${gi}sub`}>
        <td style={{ ...sTdR, background: "#EFF2F8", fontSize: 9.5, color: "#4A5160" }} colSpan={4}>Subtotal ({gi + 1})</td>
        <td style={{ ...sTdR, background: "#EFF2F8", fontSize: 9.5 }}><b>{fmtIDR(gsub)}</b></td>
      </tr>
    );
  });

  const recap = (
    <table style={{ width: "52%", marginLeft: "48%", borderCollapse: "collapse", marginTop: 16 }}><tbody>
      <tr><td style={{ fontSize: 10, color: "#626873", padding: "3px 0" }}>Sub Total</td>
        <td style={{ fontSize: 10, fontWeight: 700, textAlign: "right", padding: "3px 0" }}>{fmtIDR(totals.subtotal)}</td></tr>
      {form.ppn_type === "ppn" && (
        <tr><td style={{ fontSize: 10, color: "#626873", padding: "3px 0" }}>VAT 11%</td>
          <td style={{ fontSize: 10, fontWeight: 700, textAlign: "right", padding: "3px 0" }}>{fmtIDR(totals.ppn)}</td></tr>
      )}
      <tr><td style={{ fontSize: 13, fontWeight: 700, color: NAVY, borderTop: "1px solid #DDE2EC", paddingTop: 7 }}>Total</td>
        <td style={{ fontSize: 14, fontWeight: 700, color: NAVY, textAlign: "right", borderTop: "1px solid #DDE2EC", paddingTop: 7 }}>{fmtIDR(totals.grand)}</td></tr>
    </tbody></table>
  );

  return (
    <div className="flex flex-col w-full min-w-0 h-full" data-testid="po-live-preview">
      <div className="flex items-center justify-between gap-2 pl-4 pr-14 h-12 shrink-0 border-b border-[#DCE0E7] bg-white/70 backdrop-blur">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B8FE2] font-bold">Preview PDF · A4 · 2 Halaman</div>
        <div className="flex items-center gap-1">
          <button onClick={() => step(-0.1)} data-testid="po-preview-zoom-out" title="Perkecil" className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><ZoomOut className="w-4 h-4" /></button>
          <span className="text-[11px] font-bold text-[#3B4A63] font-num tabular-nums w-11 text-center" data-testid="po-preview-zoom-label">{Math.round(scale * 100)}%</span>
          <button onClick={() => step(0.1)} data-testid="po-preview-zoom-in" title="Perbesar" className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => setZoom("fit")} data-testid="po-preview-zoom-fit" title="Sesuaikan lebar" className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${zoom === "fit" ? "bg-[#3B4A63] text-white" : "hover:bg-[#E5EEFA] text-[#3B4A63]"}`}><Scan className="w-4 h-4" /></button>
          <div className="w-px h-5 bg-[#DCE0E7] mx-1" />
          <button onClick={onToggleFull} data-testid="po-preview-toggle-full" title={full ? "Tampilkan form" : "Lebar penuh"} className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center">
            {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div ref={wrapRef} className="flex-1 overflow-auto p-4">
        <div
          style={{ width: A4_W * scale, height: (A4_H * 2 + 24) * scale }}
          className={A4_W * scale <= wrapW - 32 ? "mx-auto" : ""}
        >
          <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", display: "flex", flexDirection: "column", gap: 24 }}>
            {/* ===== Page 1 — Order Sheet ===== */}
            <Sheet>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <tbody><tr>
                  <td style={{ verticalAlign: "top", width: "58%" }}>
                    <h1 style={{ fontSize: 25, fontWeight: 700, color: NAVY, margin: 0, letterSpacing: 0.3 }}>Purchase Order</h1>
                    <table style={{ borderCollapse: "collapse", marginTop: 11 }}><tbody>
                      <tr><td style={sMetaLbl}>PO No.</td><td style={sMetaVal} data-testid="po-preview-number">{form.number || "—"}</td></tr>
                      <tr><td style={sMetaLbl}>PO Date</td><td style={sMetaVal}>{dMY(form.date)}</td></tr>
                      <tr><td style={sMetaLbl}>Ref. Quotation</td><td style={sMetaVal} data-testid="po-preview-quoref">{form.quotation_ref || "—"}</td></tr>
                    </tbody></table>
                    {stamp && (
                      <div style={{
                        display: "inline-block", marginTop: 10, fontSize: 11, fontWeight: 700, letterSpacing: 3,
                        padding: "4px 13px", borderRadius: 6,
                        color: stamp === "PAID" ? "#1F8F52" : stamp === "VOID" ? "#A94B4A" : "#8A8F98",
                        border: `2px solid ${stamp === "PAID" ? "#57D28A" : stamp === "VOID" ? "#F3C6C8" : "#C7CCD6"}`,
                      }}>{stamp}</div>
                    )}
                  </td>
                  <td style={{ verticalAlign: "top", textAlign: "right", width: "42%" }}><Brand company={company} /></td>
                </tr></tbody>
              </table>

              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "13px 0", marginTop: 16, marginLeft: -13, marginRight: -13 }}>
                <tbody><tr>
                  <td style={sBox}>
                    <div style={sBoxTtl}>ISSUED BY</div>
                    <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                      <tr><td style={sKvK}>Company</td><td style={sKvV}><b>{company?.name || "-"}</b></td></tr>
                      <tr><td style={sKvK}>Address</td><td style={{ ...sKvV, whiteSpace: "pre-wrap" }}>{company?.address || "-"}</td></tr>
                      <tr><td style={sKvK}>Contact</td><td style={sKvV}>{company?.phone || company?.email || "-"}</td></tr>
                    </tbody></table>
                  </td>
                  <td style={sBox}>
                    <div style={sBoxTtl}>VENDOR</div>
                    <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                      <tr><td style={sKvK}>Vendor</td><td style={sKvV}><b>{vendor.name || "-"}</b></td></tr>
                      <tr><td style={sKvK}>Address</td><td style={{ ...sKvV, whiteSpace: "pre-wrap" }}>{vendor.address || "-"}</td></tr>
                      <tr><td style={sKvK}>Contact</td><td style={sKvV}>{vendor.contact || "-"}</td></tr>
                    </tbody></table>
                  </td>
                </tr></tbody>
              </table>

              <div style={{ fontSize: 9.5, color: "#5B616E", lineHeight: 1.6, marginTop: 26, textAlign: "justify" }} data-testid="po-preview-intro"><b>{company?.name || "-"}</b>{INTRO_TAIL}</div>

              <div style={{ marginTop: 22, background: "#EEF2F9", borderLeft: `4px solid ${NAVY}`, borderRadius: "0 10px 10px 0", padding: "11px 14px" }} data-testid="po-preview-project">
                <div style={{ fontSize: 7.5, letterSpacing: 2.2, color: "#7C8598", fontWeight: 700 }}>PROJECT</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#14161F", marginTop: 3 }}>{form.project_title || "—"}</div>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 22 }}>
                <tbody><tr>
                  <td style={{ width: "47%", verticalAlign: "top", paddingRight: 20 }}>
                    <div data-testid="po-preview-deadline">
                      <div style={sSectHead}>DELIVERY DEADLINE</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#14161F" }}>{dMY(form.deadline)}</div>
                    </div>
                    <div style={{ marginTop: 14 }} data-testid="po-preview-shipto">
                      <div style={sSectHead}>SHIP TO</div>
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <div style={{ flexShrink: 0, width: 34, height: 34, background: "#E5EEFA", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="15" height="19" viewBox="0 0 44 58">
                            <circle cx="22" cy="20" r="20" fill="#3B4A63" />
                            <polygon points="4,29 40,29 22,57" fill="#3B4A63" />
                            <circle cx="22" cy="19" r="9.5" fill="#E5EEFA" />
                          </svg>
                        </div>
                        <div style={{ fontSize: 9.5, color: "#5B616E", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{shipTo}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ width: "53%", verticalAlign: "top" }}>
                    <div style={{ background: NAVY, borderRadius: 12, padding: "14px 16px", color: "#fff" }} data-testid="po-preview-price">
                      <div style={{ fontSize: 7.5, letterSpacing: 2.2, color: "#B9C6DD" }}>TOTAL ORDER VALUE</div>
                      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}><tbody>
                        <tr><td style={{ fontSize: 9.5, color: "#CBD6E8", padding: "2.5px 0" }}>Sub Total</td>
                          <td style={{ fontSize: 9.5, fontWeight: 700, textAlign: "right", padding: "2.5px 0" }}>{fmtIDR(totals.subtotal)}</td></tr>
                        {form.ppn_type === "ppn" && (
                          <tr><td style={{ fontSize: 9.5, color: "#CBD6E8", padding: "2.5px 0" }}>VAT 11%</td>
                            <td style={{ fontSize: 9.5, fontWeight: 700, textAlign: "right", padding: "2.5px 0" }}>{fmtIDR(totals.ppn)}</td></tr>
                        )}
                        <tr><td style={{ fontSize: 14, fontWeight: 700, borderTop: "1px solid #6B7C99", paddingTop: 7 }}>Total</td>
                          <td style={{ fontSize: 15, fontWeight: 700, textAlign: "right", borderTop: "1px solid #6B7C99", paddingTop: 7 }}>{fmtIDR(totals.grand)}</td></tr>
                      </tbody></table>
                      <div style={{ borderTop: "1px dashed #6B7C99", marginTop: 9, paddingTop: 7 }}>
                        <div style={{ fontSize: 7.5, letterSpacing: 2, color: "#B9C6DD" }}>TOTAL (IN WORDS)</div>
                        <div style={{ fontSize: 10, fontWeight: 700, lineHeight: 1.45, marginTop: 3 }}>{terbilang(totals.grand)} Rupiah</div>
                      </div>
                    </div>
                  </td>
                </tr></tbody>
              </table>

              <div style={{ marginTop: 14 }} data-testid="po-preview-terms">
                <div style={sSectHead}>TERMS AND CONDITIONS</div>
                {termGroups.length === 0 ? (
                  <div style={{ fontSize: 9.5, color: "#A0A3A8" }}>—</div>
                ) : termGroups.map((g, gi) => (
                  <div key={gi} style={{ display: "flex", gap: 0, marginBottom: 1 }}>
                    <span style={{ width: 16, flexShrink: 0, color: NAVY, fontWeight: 700, fontSize: 10, lineHeight: 1.35 }}>{gi + 1}.</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: "#14161F", lineHeight: 1.35 }}>{g.title}</div>
                      {(g.items || []).filter((t) => (t || "").trim()).map((t, ii) => (
                        <div key={ii} style={{ display: "flex", gap: 5, paddingLeft: 16 }}>
                          <span style={{ color: "#5B616E", fontSize: 9.5, lineHeight: 1.35, flexShrink: 0 }}>&ndash;</span>
                          <span style={{ color: "#5B616E", fontSize: 9.5, lineHeight: 1.35 }}>{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 26 }}>
                <tbody><tr>
                  <td />
                  {form.vendor_sign && (
                    <td style={{ width: 215, paddingRight: 34, verticalAlign: "bottom", whiteSpace: "nowrap" }} data-testid="po-preview-vendor-signature">
                      <div style={{ fontSize: 9, color: "#5B616E" }}>Acknowledged by,</div>
                      <div style={{ fontSize: 9.5, fontWeight: 700, color: "#14161F", marginTop: 1 }}>{vendor.name || "-"}</div>
                      <div style={{ width: 150, height: 56, borderBottom: "1px solid #C7CCD6" }} />
                      <div style={{ fontSize: 10, fontWeight: 700, marginTop: 5 }}>{form.vendor_sign_name || "-"}</div>
                      <div style={{ fontSize: 8.5, color: "#8A8F98", marginTop: 1 }}>{form.vendor_sign_title || "\u00A0"}</div>
                    </td>
                  )}
                  <td style={{ width: 170, paddingRight: 18, verticalAlign: "bottom", whiteSpace: "nowrap" }} data-testid="po-preview-signature">
                    {form.vendor_sign && (
                      <>
                        <div style={{ fontSize: 9, color: "#5B616E" }}>Issued by,</div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: "#14161F", marginTop: 1 }}>{company?.name || "-"}</div>
                      </>
                    )}
                    <div style={{ width: 150, height: 56, borderBottom: "1px solid #C7CCD6" }} />
                    <div style={{ fontSize: 10, fontWeight: 700, marginTop: 5 }}>{ownerName || "-"}</div>
                    <div style={{ fontSize: 8.5, color: "#8A8F98", marginTop: 1 }}>{ownerJobTitle || "Owner"}</div>
                  </td>
                  <td style={{ width: 80, textAlign: "right", verticalAlign: "bottom" }} data-testid="po-preview-qr">
                    {qrDataUri
                      ? <img src={qrDataUri} alt="QR" style={{ width: 66, height: 66, display: "inline-block" }} />
                      : <div style={{ width: 66, height: 66, border: "1px dashed #C7CCD6", display: "inline-block" }} />}
                    <div style={{ fontSize: 7, color: "#8A8F98", letterSpacing: 0.8, marginTop: 5, lineHeight: 1.35, textAlign: "right" }}>
                      {qrDataUri ? <>SCAN OR CLICK QR<br />TO VERIFY</> : "QR TERSEDIA SETELAH DISIMPAN"}
                    </div>
                  </td>
                </tr></tbody>
              </table>

              <Footer company={company} page={1} />
            </Sheet>

            {/* ===== Page 2 — Lampiran item ===== */}
            <Sheet>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <tbody><tr>
                  <td style={{ verticalAlign: "top", width: "62%" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Attachment</div>
                    <div style={{ fontSize: 8.5, color: "#8A8F98", marginTop: 3 }}>Detailed Scope of Work, Technical Specifications, and Price Breakdown</div>
                    <div style={{ fontSize: 8.5, color: "#A0A3A8", marginTop: 2 }}>PO {form.number || "—"}</div>
                  </td>
                  <td style={{ verticalAlign: "top", textAlign: "right", width: "38%" }}><Brand company={company} size={220} /></td>
                </tr></tbody>
              </table>

              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 15 }} data-testid="po-preview-items">
                <thead><tr>
                  <th style={{ ...sTh, width: 24, textAlign: "center" }}>#</th>
                  <th style={sTh}>ITEM DESCRIPTION</th>
                  <th style={{ ...sTh, width: 74, textAlign: "center" }}>VOLUME</th>
                  <th style={{ ...sTh, width: 92, textAlign: "right" }}>RATE</th>
                  <th style={{ ...sTh, width: 102, textAlign: "right" }}>AMOUNT</th>
                </tr></thead>
                <tbody>{rows}</tbody>
              </table>

              {recap}
              <Footer company={company} page={2} />
            </Sheet>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PoPdfPreview;
